import React, { useState, useEffect } from "react";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link, useNavigate } from "react-router-dom";
import { fetchAPI } from "../utils/api";
import asimg from "../assets/images/asimg.png";

// Helper to parse HTML option strings generated by CKEditor
function parseHTMLOption(htmlString) {
  if (!htmlString) return { title: "", desc: "" };
  const tempDiv = document.createElement("div");
  // Replace <br> or <br/> with newline so we can split easily
  const cleanedHtml = htmlString.replace(/<br\s*\/?>/gi, "\n");
  tempDiv.innerHTML = cleanedHtml;
  
  // Extract all non-empty text lines
  const text = tempDiv.textContent || tempDiv.innerText || "";
  const lines = text
    .split("\n")
    .map(line => line.trim())
    .filter(line => line.length > 0);
    
  const title = lines[0] || "";
  const desc = lines.slice(1).join(" ") || "";
  
  return { title, desc };
}

// Helper to dynamically assign emojis based on option titles
function getIconForOption(title) {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes("injection")) return "💉";
  if (lowerTitle.includes("wound") || lowerTitle.includes("dressing")) return "🩹";
  if (lowerTitle.includes("elderly") || lowerTitle.includes("senior") || lowerTitle.includes("older")) return "👵";
  if (lowerTitle.includes("daily")) return "📅";
  if (lowerTitle.includes("weekly")) return "🗓️";
  if (lowerTitle.includes("monthly")) return "📆";
  if (lowerTitle.includes("one time") || lowerTitle.includes("once") || lowerTitle.includes("single")) return "1️⃣";
  if (lowerTitle.includes("medical") || lowerTitle.includes("setup")) return "🏥";
  return "📋"; // default emoji
}

export default function Stepper() {
  const navigate = useNavigate();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState("");

  useEffect(() => {
    async function loadAssessment() {
      try {
        const data = await fetchAPI("icu-assessment");
        if (data && data.status === 200 && Array.isArray(data.result)) {
          // Format assessments to React steps structure
          const formattedSteps = data.result.map((item) => {
            let rawOptions = [];
            if (Array.isArray(item.iass_description)) {
              rawOptions = item.iass_description;
            } else if (typeof item.iass_description === "object" && item.iass_description !== null) {
              rawOptions = Object.values(item.iass_description);
            } else {
              try {
                rawOptions = JSON.parse(item.iass_description) || [];
              } catch (e) {
                rawOptions = [];
              }
            }
            
            const options = rawOptions.map((optHtml) => {
              const parsed = parseHTMLOption(optHtml);
              return {
                title: parsed.title,
                desc: parsed.desc,
                icon: getIconForOption(parsed.title),
              };
            });

            return {
              question: item.iass_title,
              description: item.iass_subtitle,
              options: options,
            };
          });

          // Filter out steps with status 1 (active) from backend
          setSteps(formattedSteps);
        } else {
          setError("Failed to load assessments.");
        }
      } catch (err) {
        console.error("Error fetching assessment:", err);
        setError("Error loading assessments. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    loadAssessment();
  }, []);

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
      setSelectedOption(""); // Reset selection for next step
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      setSelectedOption(""); // Reset selection for previous step
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="assessment">
          <div className="container text-center py-5">
            <h3>Loading Assessment Setup...</h3>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || steps.length === 0) {
    return (
      <>
        <Header />
        <div className="assessment">
          <div className="container text-center py-5">
            <h3>{error || "No Assessments Configured."}</h3>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const stepData = steps[currentStep - 1];

  return (
   <>
    <Header/>
    <div className="assessment">
      <div className="container">
        <div className="stepper-wrapper">
          {/* Top Stepper */}
          <div className="stepper-header">
            <p className="short-title">ICU Setup Assessment</p>

            <div className="stepper-progress">
              {steps.map((_, index) => (
                <div key={index} className="step-item">
                  <div
                    className={`step-circle 
                  ${index + 1 < currentStep ? "completed" : ""}
                  ${index + 1 === currentStep ? "active" : ""}`}
                  >
                    {index + 1 < currentStep ? (
                      <DoneOutlinedIcon />
                    ) : (
                      `0${index + 1}`
                    )}
                  </div>

                  {index !== steps.length - 1 && (
                    <div
                      className={`step-line ${
                        index + 1 < currentStep ? "filled" : ""
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="stepper-body">
            <div className="row align-items-center">
              <div className="col-lg-7">
                <h2>
                  Q.{currentStep} {stepData.question}
                </h2>

                <p className="step-desc">{stepData.description}</p>
              </div>

              <div className="col-lg-5 text-center">
                <img
                  src={asimg}
                  alt=""
                  className="ass-img"
                />
              </div>
            </div>

            {/* Options */}
            {stepData.options.length > 0 && (
              <div className="options-grid">
                {stepData.options.map((option, index) => (
                  <label
                    key={index}
                    className={`option-card ${
                      selectedOption === option.title ? "selected" : ""
                    }`}
                  >
                    <div className="option-icon">{option.icon}</div>

                    <div className="option-content">
                      <h5>{option.title}</h5>
                      <p>{option.desc}</p>
                    </div>

                    <input
                      type="radio"
                      name={`step-${currentStep}`}
                      value={option.title}
                      checked={selectedOption === option.title}
                      onChange={() => setSelectedOption(option.title)}
                    />
                  </label>
                ))}
              </div>
            )}

            {/* Safety Card */}
            <div className="safety-box">
              <GppGoodOutlinedIcon />

              <div>
                <h6>Your Safety is our priority</h6>
                <p>
                  All nurses are background verified and trained professionals.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="step-actions">
              <button className="back-btn" onClick={prevStep}  disabled={currentStep === 1}>
                BACK
              </button>

              {currentStep === steps.length ? (
                <button className="next-btn" onClick={() => navigate("/icu-home-booking")}>
                  SUBMIT
                </button>
              ) : (
                <button className="next-btn" onClick={nextStep}>
                  NEXT
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer/>
    </>
  ); 
}
